import { useEffect, useMemo } from 'react';
import {
  useSelectedEntityId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import request from 'utils/request';
import { showSnackbar, useMaskMode } from 'stores/useUiStore';
import { TOKEN_INVALID } from 'containers/Base/useBaseViewData';
import replaceByKeyName from 'utils/mockFn';
import type { ApiRespBody } from 'types/api';

import type {
  Entity,
  EntitiesState,
  TEntityPredicateState,
} from '../entities.types';


export const ENTITIES_QUERY_KEY = ['entities'] as const;

export async function fetchEntities(
  authToken: string,
  signal?: AbortSignal
): Promise<Entity[]> {
  const result = await request<ApiRespBody<{ entities: Entity[] }>>(
    '/api/entities',
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', authorization: authToken },
      ...(signal ? { signal } : {}),
    }
  );

  if (result?.success) return result.data?.entities ?? [];
  if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result?.message || 'Failed to load entities');
}

export function entitiesQueryOptions(
  authToken: string,
  selectedEntityId: number | string | null
) {
  return queryOptions({
    queryKey: [...ENTITIES_QUERY_KEY, selectedEntityId],
    queryFn: ({ signal }) => fetchEntities(authToken, signal),
    staleTime: Infinity,
    enabled: !!authToken,
  });
}

function maskEntities(entities: Entity[]): Entity[] {
  return entities.map((entity) => {
    const masked = { ...entity };
    replaceByKeyName(masked, 'entityName', 'organization');
    replaceByKeyName(masked, 'entityDescription', 'organization');
    return masked;
  });
}

export type UseEntitiesResult = EntitiesState['entities'] & {
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export function useEntities(): UseEntitiesResult {
  const authToken = useAuthtoken();
  const selectedEntityId = useSelectedEntityId();
  const maskMode = useMaskMode();

  const query = useQuery(entitiesQueryOptions(authToken, selectedEntityId));

  useEffect(() => {
    if (!(query.error instanceof Error)) return;
    if (query.error.message === TOKEN_INVALID) {
      logout();
    } else {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onEntitiesGet',
          open: true,
          message: query.error.message,
          severity: 'error',
        });
    }
  }, [query.error]);

  const values = useMemo(() => {
    const raw = query.data ?? [];
    return maskMode ? maskEntities(raw) : raw;
  }, [query.data, maskMode]);

  const lastUpdated = useMemo(
    () =>
      query.dataUpdatedAt
        ? new Date(query.dataUpdatedAt).toISOString()
        : undefined,
    [query.dataUpdatedAt]
  );

  return useMemo(
    () => ({
      values,
      lastUpdated,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      refetch: () => {
        query.refetch();
      },
    }),
    [values, lastUpdated, query]
  );
}

function useEntityWrite<TInput>(opts: {
  caller: string;
  successMessage: string;
  sendRequest: (
    input: TInput,
    authToken: string
  ) => Promise<ApiRespBody<unknown> | null>;
}) {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: TInput) => opts.sendRequest(input, authToken),
    onSuccess: (result) => {
      if (result?.success) {
        showSnackbar({
            autoHideDuration: 5000,
            caller: opts.caller,
            open: true,
            message: opts.successMessage,
            severity: 'success',
          });
        queryClient.invalidateQueries({ queryKey: ENTITIES_QUERY_KEY });
      } else if (result?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: opts.caller,
            open: true,
            message: result?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: opts.caller,
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
  });
}

export function useAddEntity() {
  return useEntityWrite<TEntityPredicateState>({
    caller: 'onEntityAdd',
    successMessage: 'Entity Added',
    sendRequest: (entity, authToken) =>
      request<ApiRespBody<unknown>>('/api/entities', {
        method: 'POST',
        body: JSON.stringify(entity),
        headers: {
          'Content-Type': 'application/json',
          authorization: authToken,
        },
      }),
  });
}

export function useUpdateEntity() {
  return useEntityWrite<Entity>({
    caller: 'onEntityUpdate',
    successMessage: 'Entity Updated',
    sendRequest: (entity, authToken) =>
      request<ApiRespBody<unknown>>(`/api/entities/${entity.entityId}`, {
        method: 'PUT',
        body: JSON.stringify(entity),
        headers: {
          'Content-Type': 'application/json',
          authorization: authToken,
        },
      }),
  });
}

export function useDeleteEntity() {
  return useEntityWrite<Entity>({
    caller: 'onEntityDelete',
    successMessage: 'Entity Deleted',
    sendRequest: (entity, authToken) =>
      request<ApiRespBody<unknown>>(`/api/entities/${entity.entityId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          authorization: authToken,
        },
      }),
  });
}
